import {
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from 'react-query';
import {
  IPost,
  IPostBE,
  TPostInfinityQuery,
  TPostQuery,
} from '../../interfaces/post.interface';
import { useEffect, useState } from 'react';
import { getPostDetail, postReadPost } from '../../apis/tms/post.api';
import { convertToIPost } from '../../common/post/convertToIPost';
import { deepClone } from '../../common/other';

interface IUsePostManagementWithoutInfinityQuery {
  queryKey: QueryKey;
  queryFn: QueryFunction<TPostQuery, QueryKey>;
  opt?: Omit<
    UseQueryOptions<TPostQuery, Error, TPostQuery, QueryKey>,
    'queryKey' | 'queryFn'
  >;
}

interface IUsePostManagementWithInfinityQuery {
  queryKey: QueryKey;
  queryFn: QueryFunction<TPostInfinityQuery, QueryKey>;
  opt?: Omit<
    UseInfiniteQueryOptions<
      TPostInfinityQuery,
      Error,
      TPostInfinityQuery,
      TPostInfinityQuery,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >;
}

export enum EPostEvent {
  // eslint-disable-next-line no-unused-vars
  Add = 'add',
  // eslint-disable-next-line no-unused-vars
  Delete = 'delete',
  // eslint-disable-next-line no-unused-vars
  Update = 'update',
}
const messageKey = 'TransferPostId';
export const postMessagePostId = (postId: string, event: EPostEvent) =>
  window.postMessage(
    { message: messageKey, postId, event },
    window.location.origin
  );

// this use for normal query
const query = (
  queryKey: QueryKey,
  queryFn: QueryFunction<TPostQuery, QueryKey>,
  opt?: Omit<
    UseQueryOptions<TPostQuery, Error, TPostQuery, QueryKey>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery<TPostQuery, Error, TPostQuery, QueryKey>(queryKey, queryFn, {
    ...opt,
    retry: 3,
  });

const usePostManagementWithoutInfinityQuery = ({
  queryFn,
  queryKey,
  opt,
}: IUsePostManagementWithoutInfinityQuery) => {
  const [postItems, setPostItems] = useState<IPost[]>([]);
  const [canContinueFetch, setCanContinueFetch] = useState<boolean>(true);
  const queryResult = query(queryKey, queryFn, {
    ...opt,
    onSettled(data, error) {
      opt?.onSettled && opt?.onSettled(data, error);

      if (data) {
        if (data) {
          setPostItems((prev) => [...prev, ...data]);
          if (data.length === 0) setCanContinueFetch(false);
        }
      }
    },
  });

  useEffect(() => {
    if (queryResult.isLoading) {
      setPostItems([]);
    }
  }, [queryResult.isLoading]);

  useEffect(() => {
    const handleListenEvent = async (e: MessageEvent<any>) => {
      if (
        e.origin === window.location.origin &&
        e.data?.message === messageKey
      ) {
        const hashHandler = {
          [EPostEvent.Add]: () => addNewPost(e.data?.postId),
          [EPostEvent.Delete]: () => deletePost(e.data?.postId),
          [EPostEvent.Update]: () => updatePost(e.data?.postId),
        };
        await hashHandler[e.data?.event as EPostEvent]();
      }
    };

    window.addEventListener('message', handleListenEvent);

    return () => {
      window.removeEventListener('message', handleListenEvent);
    };
  }, []);

  // because
  const addNewPost = async (postId: string) => {
    try {
      const res = await (
        getPostDetail(postId) as unknown as Promise<IPostBE>
      ).then((value) => convertToIPost(value));

      // avoid duplicate if continue fetch
      await postReadPost(postId);

      setPostItems((prev) => [res, ...prev]);
    } catch (error) {
      console.log(error);
    }
  };

  const deletePost = (postId: string) => {
    setPostItems((prev) => {
      const clone = deepClone(prev);
      return clone.filter((item) => item.postId !== postId);
    });
  };

  const updatePost = async (postId: string) => {
    try {
      const res = await (
        getPostDetail(postId) as unknown as Promise<IPostBE>
      ).then((value) => convertToIPost(value));
      setPostItems((prev) =>
        prev.map((item) => (item.postId === postId ? res : item))
      );
    } catch (error) {
      console.log(error);
    }
  };

  return {
    postItems,
    canContinueFetch,
    queryResult,
  };
};

// this use for infinity query
const infinityQuery = (
  queryKey: QueryKey,
  queryFn: QueryFunction<TPostInfinityQuery, QueryKey>,
  opt?: Omit<
    UseInfiniteQueryOptions<
      TPostInfinityQuery,
      Error,
      TPostInfinityQuery,
      TPostInfinityQuery,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >
) =>
  useInfiniteQuery<TPostInfinityQuery, Error, TPostInfinityQuery, QueryKey>(
    queryKey,
    queryFn,
    { ...opt, retry: 3 }
  );

const usePostManagementWithInfinityQuery = ({
  queryFn,
  queryKey,
  opt,
}: IUsePostManagementWithInfinityQuery) => {
  const [postItems, setPostItems] = useState<IPost[]>([]);
  const queryResult = infinityQuery(queryKey, queryFn, {
    ...opt,
    select: (data): any => {
      const lastPages = data.pages.length - 1;
      return {
        data: data.pages[lastPages].data,
        meta: {
          totalCount: data.pages[lastPages]?.totalCount || 0,
          hasNextPage: data.pages[lastPages]?.hasNextPage || false,
          pageSize: data.pages[lastPages]?.pageSize,
          currentPage: data.pages[lastPages]?.currentPage,
        },
      };
    },
  });

  useEffect(() => {
    let clone = queryResult.data as any;
    setPostItems((prev) =>
      !clone?.data
        ? prev
        : clone?.data?.length === 0
        ? clone?.data
        : [...prev, ...clone.data]
    );
  }, [queryResult.data]);

  useEffect(() => {
    if (queryResult.isLoading) {
      setPostItems([]);
    }
  }, [queryResult.isLoading]);

  useEffect(() => {
    const handleListenEvent = async (e: MessageEvent<any>) => {
      if (
        e.origin === window.location.origin &&
        e.data?.message === messageKey
      ) {
        const hashHandler = {
          [EPostEvent.Add]: () => addNewPost(e.data?.postId),
          [EPostEvent.Delete]: () => deletePost(e.data?.postId),
          [EPostEvent.Update]: () => updatePost(e.data?.postId),
        };
        await hashHandler[e.data?.event as EPostEvent]();
      }
    };

    window.addEventListener('message', handleListenEvent);

    return () => {
      window.removeEventListener('message', handleListenEvent);
    };
  }, []);

  // because
  const addNewPost = async (postId: string) => {
    try {
      const res = await (
        getPostDetail(postId) as unknown as Promise<IPostBE>
      ).then((value) => convertToIPost(value));

      // avoid duplicate if continue fetch
      await postReadPost(postId);

      setPostItems((prev) => [res, ...prev]);
    } catch (error) {
      console.log(error);
    }
  };

  const deletePost = (postId: string) => {
    setPostItems((prev) => {
      const clone = deepClone(prev);
      return clone.filter((item) => item.postId !== postId);
    });
  };

  const updatePost = async (postId: string) => {
    try {
      const res = await (
        getPostDetail(postId) as unknown as Promise<IPostBE>
      ).then((value) => convertToIPost(value));
      setPostItems((prev) =>
        prev.map((item) => (item.postId === postId ? res : item))
      );
    } catch (error) {
      console.log(error);
    }
  };

  return {
    postItems,
    queryResult,
  };
};

export {
  usePostManagementWithoutInfinityQuery,
  usePostManagementWithInfinityQuery,
};
